import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../../helpers';
import { ONBOARDING_QUESTIONNAIRES_API_BASE } from '../../constants';
import { buildOnboardingQuestionnaireDocumentFormData } from './utils';

import type { IOnboardingQuestionnaireDocument } from '../../../../../pages/questionnaires/onboarding/types';
import type { GetOnboardingQuestionnaireByIdPayload } from '../../types';
import type {
  DeleteOnboardingQuestionnaireDocumentPayload,
  GetOnboardingQuestionnaireDocumentDetailsPayload,
  UploadOnboardingQuestionnaireDocumentPayload,
} from './types';

export function createOnboardingDocumentsThunks() {
  return {
    uploadOnboardingQuestionnaireDocument: uploadOnboardingQuestionnaireDocument(),
    getOnboardingQuestionnaireDocumentDetails: getOnboardingQuestionnaireDocumentDetails(),
    getOnboardingQuestionnaireDocuments: getOnboardingQuestionnaireDocuments(),
    deleteOnboardingQuestionnaireDocument: deleteOnboardingQuestionnaireDocument(),
  };
}

function getOnboardingQuestionnaireDocumentDetails() {
  return createAsyncThunk<
    IOnboardingQuestionnaireDocument,
    GetOnboardingQuestionnaireDocumentDetailsPayload
  >(
    'onboardingQuestionnaire/getOnboardingQuestionnaireDocumentDetails',
    async ({ onboardingQuestionnaireId, documentId, getAccessTokenSilently }, { signal }) => {
      const response: IOnboardingQuestionnaireDocument = await fetchWrapper.get({
        url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/documents/${documentId}`,
        getAccessTokenSilently,
        // Forward the thunk's signal so request.abort() also cancels the HTTP request.
        signal,
      });
      return response;
    },
  );
}

function getOnboardingQuestionnaireDocuments() {
  return createAsyncThunk<
    IOnboardingQuestionnaireDocument[],
    GetOnboardingQuestionnaireByIdPayload
  >(
    'onboardingQuestionnaire/getOnboardingQuestionnaireDocuments',
    async ({ onboardingQuestionnaireId, getAccessTokenSilently }) => {
      const response: IOnboardingQuestionnaireDocument[] = await fetchWrapper.get({
        url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/documents/`,
        getAccessTokenSilently,
      });
      return response;
    },
  );
}

function deleteOnboardingQuestionnaireDocument() {
  return createAsyncThunk<void, DeleteOnboardingQuestionnaireDocumentPayload>(
    'onboardingQuestionnaire/deleteOnboardingQuestionnaireDocument',
    async ({ onboardingQuestionnaireId, documentId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/documents/${documentId}`,
        getAccessTokenSilently,
      });
    },
  );
}

function uploadOnboardingQuestionnaireDocument() {
  return createAsyncThunk<
    IOnboardingQuestionnaireDocument[],
    UploadOnboardingQuestionnaireDocumentPayload
  >(
    'onboardingQuestionnaire/uploadOnboardingQuestionnaireDocument',
    async ({ onboardingQuestionnaireId, data, getAccessTokenSilently }) => {
      const formData = buildOnboardingQuestionnaireDocumentFormData(data);
      const response: IOnboardingQuestionnaireDocument[] = await fetchWrapper.post(
        {
          url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/documents/`,
          getAccessTokenSilently,
        },
        formData,
        'multipart/form-data',
      );
      return response;
    },
  );
}
